import React, { useEffect, useState } from 'react';
import { PriorityGoal } from '../../types';

interface PriorityModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (priority: PriorityGoal) => void;
  onDelete?: (priority: PriorityGoal) => void;
  priority: PriorityGoal | null;
}

const PriorityModal: React.FC<PriorityModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onDelete,
  priority
}) => {
  const [name, setName] = useState<string>('');
  const [pillarAlignment, setPillarAlignment] = useState<string>('');
  const [details, setDetails] = useState<string[]>(['']);
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [charCounts, setCharCounts] = useState<number[]>([0]);
  const maxChars = 500;

  // Reset form when modal is closed or opened
  useEffect(() => {
    if (isOpen) {
      if (priority) {
        setName(priority.name);
        setPillarAlignment(priority.pillar_alignment);
        setDetails(priority.details.length > 0 ? priority.details : ['']);
        setCharCounts(priority.details.map(detail => detail.length));
      } else {
        resetForm();
      }
    }
  }, [isOpen, priority]);

  // Function to reset form fields to initial values
  const resetForm = () => {
    setName('');
    setPillarAlignment('');
    setDetails(['']);
    setFormErrors({});
    setCharCounts([0]);
  };

  // Handle escape key press
  useEffect(() => {
    if (!isOpen) return;
    
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    // Update the corresponding state based on the input name
    if (name === 'name') {
      setName(value);
    } else if (name === 'pillarAlignment') {
      setPillarAlignment(value);
    }

    // Clear error when field is edited
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleDetailChange = (index: number, value: string) => {
    const newDetails = [...details];
    newDetails[index] = value;
    setDetails(newDetails);

    const newCharCounts = [...charCounts];
    newCharCounts[index] = value.length;
    setCharCounts(newCharCounts);

    // Clear error when field is edited
    if (formErrors[`detail-${index}`]) {
      setFormErrors(prev => ({ ...prev, [`detail-${index}`]: '' }));
    }
  };

  const addBulletPoint = () => {
    setDetails([...details, '']);
    setCharCounts([...charCounts, 0]);
  };

  const removeBulletPoint = (index: number) => {
    if (details.length > 1) {
      const newDetails = details.filter((_, i) => i !== index);
      const newCharCounts = charCounts.filter((_, i) => i !== index);
      setDetails(newDetails);
      setCharCounts(newCharCounts);
    }
  };

  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {};

    if (!name?.trim()) {
      errors.name = 'Priority name is required';
    }

    if (!pillarAlignment?.trim()) {
      errors.pillarAlignment = 'Priority caption is required';
    }

    // Check if at least one detail is provided
    const hasValidDetail = details.some(detail => detail?.trim().length > 0);
    if (!hasValidDetail) {
        errors['detail-0'] = 'At least one Description point is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm() && !isSubmitting) {
      setIsSubmitting(true);
      
      // Filter out empty details
      const filteredDetails = details.filter(detail => detail?.trim().length > 0);
      
      // Call onSave and handle the promise
      Promise.resolve(onSave({
        name,
        pillar_alignment: pillarAlignment,
        details: filteredDetails
      }))
      .finally(() => {
        resetForm();
        setIsSubmitting(false);
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-end z-50">
      <div className="bg-white w-full max-w-md h-full overflow-y-auto shadow-xl">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-[#0F2A4F]">
              {priority ? 'Edit Priority' : 'Add Priority'}
            </h2>
            <button
              onClick={onClose}
              className="rounded-full p-2 hover:bg-gray-100"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <input
                type="text"
                name="name"
                placeholder="Priority name"
                value={name}
                onChange={handleChange}
                className={`w-full px-3 py-2 border ${formErrors.name ? 'border-red-500' : 'border-gray-300'} rounded-md`}
              />
              {formErrors.name && (
                <p className="mt-1 text-sm text-red-500">{formErrors.name}</p>
              )}
            </div>

            <div className="mb-4">
              <input
                type="text"
                name="pillarAlignment"
                placeholder="Priority caption"
                value={pillarAlignment}
                onChange={handleChange}
                className={`w-full px-3 py-2 border ${formErrors.pillarAlignment ? 'border-red-500' : 'border-gray-300'} rounded-md`}
              />
              {formErrors.pillarAlignment && (
                <p className="mt-1 text-sm text-red-500">{formErrors.pillarAlignment}</p>
              )}
            </div>

            {/* Bullet Points */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                              Description Points
              </label>
              
              {details.map((detail, index) => (
                <div key={index} className="mb-3 flex items-start">
                  <textarea
                          placeholder={`Description point ${index + 1}`}
                    value={detail}
                    onChange={(e) => handleDetailChange(index, e.target.value)}
                    className={`w-full px-3 py-2 border ${formErrors[`detail-${index}`] ? 'border-red-500' : 'border-gray-300'} rounded-md h-24`}
                    maxLength={maxChars}
                  />
                  <button
                    type="button"
                    onClick={() => removeBulletPoint(index)}
                    disabled={details.length <= 1}
                    className={`ml-2 p-2 text-red-500 ${details.length <= 1 ? 'opacity-50 cursor-not-allowed' : 'hover:text-red-700'}`}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              ))}
              
              {formErrors['detail-0'] && (
                <p className="mt-1 text-sm text-red-500">{formErrors['detail-0']}</p>
              )}
              
              <button
                type="button"
                onClick={addBulletPoint}
                className="mt-2 flex items-center text-[#0F2A4F] hover:text-[#204374]"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                              Add Description
              </button>
            </div>

                      <div className={`mt-6 flex justify-end ${priority && onDelete && 'justify-between'}`}>
              {/* Delete button - only show for existing priorities */}
              {priority && onDelete && (
                <button
                  type="button"
                  onClick={() => {
                    if (priority && onDelete) {
                      onDelete(priority);
                      onClose();
                    }
                  }}
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  Delete
                </button>
              )}
              
              <div className="flex">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isSubmitting}
                  className={`px-4 py-2 border border-gray-300 rounded-md mr-2 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'}`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`px-4 py-2 bg-[#0F2A4F] text-white rounded-md flex items-center ${isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:bg-[#204374]'}`}
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                      {priority ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    priority ? 'Confirm' : 'Add Priority'
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PriorityModal;
